import { Controller } from "@hotwired/stimulus";
import * as d3 from "d3";
import moment from "moment";
import Interpolator from "../lib/interpolator.js";

export default class extends Controller {
  static values = {
    startAt: String,
    endAt: String,
    meterReadings: Array,
    marketPrices: Array,
  };

  connect() {
    const marketPrices = this.marketPricesValue.map((price) => {
      return {
        timestamp: moment(price.timestamp),
        price: price.price,
      };
    });

    const svg = d3
      .select(this.element)
      .append("svg")
      .attr("width", this.width + this.margin.left + this.margin.right)
      .attr("height", this.height + this.margin.top + this.margin.bottom)
      .append("g")
      .attr("transform", "translate(" + this.margin.left + "," + this.margin.top + ")");

    const xDomain = [moment(this.startAtValue), moment(this.endAtValue)];
    const x = d3.scaleUtc().domain(xDomain).range([0, this.width]);

    let yMeterReadings = d3
      .scaleLinear()
      .domain(d3.extent(this.meterReadings.map((v) => v.reading_kwh)))
      .range([this.height, 0]);

    const priceDomain = d3.extent(marketPrices.map((v) => v.price));

    const priceMargin = (priceDomain[1] - priceDomain[0]) * 0.1;
    priceDomain[0] -= priceMargin;
    priceDomain[1] += priceMargin;

    let yMarketPrices = d3.scaleLinear().domain(priceDomain).range([this.height, 0]);

    const numberOfBars = 45;
    const numberOfSeconds = xDomain[1].diff(xDomain[0], "seconds");
    const secondsPerBar = numberOfSeconds / numberOfBars;
    const pixelsPerBar = this.width / numberOfBars;

    const xAxisTicks = Array.from({ length: numberOfBars }, (v, i) =>
      xDomain[0].clone().add(i * secondsPerBar, "seconds"),
    );

    [this.uncertifiedMeterReadings, this.certifiedMeterReadings].forEach((meterReadings) => {
      const dataForInterpolator = meterReadings.map((reading) => [reading.timestamp, reading.reading_kwh]);
      const interpolator = new Interpolator(dataForInterpolator);

      svg
        .selectAll("bar")
        .data(xAxisTicks)
        .enter()
        .append("rect")
        .attr("class", (d, i) => {
          const moment = xAxisTicks[i];
          const current = interpolator.interpolate(moment);

          const previousMoment = xAxisTicks[i - 1];
          const previous = interpolator.interpolate(previousMoment);

          const nextMoment = xAxisTicks[i + 1];
          const next = interpolator.interpolate(nextMoment);

          return previous < current || current < next ? "fill-energy-fill" : "fill-detail-4";
        })
        .attr("rx", 3)
        .attr("x", (time) => x(time) - pixelsPerBar / 2)
        .attr("y", (time) => yMeterReadings(interpolator.interpolate(time)))
        .attr("width", pixelsPerBar - 2)
        .attr("height", (time) => this.height - yMeterReadings(interpolator.interpolate(time)));
    });

    const powerMarketPricesLine = svg
      .datum(marketPrices)
      .append("g")
      .call((g) => {
        return g
          .append("path")
          .attr("class", "stroke-detail-2")
          .attr("fill", "none")
          .attr("stroke-width", 2)
          .attr(
            "d",
            d3
              .line()
              .curve(d3.curveStepAfter)
              .x((d) => x(d.timestamp))
              .y((d) => {
                return yMarketPrices(d.price);
              }),
          );
      });

    const everyHour = d3.timeHour.every(6).range(...xDomain);

    const xAxis = svg
      .append("g")
      .attr("class", "text-detail-3")
      .attr("transform", "translate(0," + this.height + ")")
      .call(
        d3
          .axisBottom(x)
          .tickValues(everyHour)
          .tickFormat((d) => moment(d).format("H\\h")),
      )
      .call((g) => g.selectAll(".tick line").clone().attr("stroke-opacity", 0.2).attr("y", 0).attr("y2", -this.height))
      .call((g) => g.selectAll("text").attr("x", 4).style("text-anchor", "start"))
      .call((g) =>
        g.selectAll(".tick line:first-of-type").classed("stroke-current text-detail-3", true).attr("y2", 16),
      );

    const midnights = d3.timeHour.every(24).range(...xDomain);
    svg
      .append("g")
      .attr("class", "text-detail-3")
      .attr("transform", "translate(0," + this.height + ")")
      .call(
        d3
          .axisBottom(x)
          .tickValues(midnights)
          .tickFormat((d) => moment(d).format("LL")),
      )
      .call((g) => g.selectAll("text").attr("x", 4).attr("y", 22).style("text-anchor", "start"))
      .call((g) =>
        g
          .selectAll(".tick line:first-of-type")
          .classed("stroke-current text-detail-2", true)
          .attr("y", 16)
          .attr("y2", 40),
      );

    const yAxis = svg
      .append("g")
      .attr("class", "text-detail-3")
      .attr("transform", "translate(" + this.width + ", 0)")
      .call(
        d3
          .axisRight(yMeterReadings)
          .ticks(8)
          .tickFormat((d) => d),
      )
      .call((g) => {
        return g
          .selectAll(".tick line")
          .clone()
          .attr("stroke-opacity", (d) => (d === 0 ? 1 : 0.1))
          .attr("x", 0)
          .attr("x2", -this.width);
      });

    const yLabel = svg
      .append("g")
      .attr("transform", "translate(" + (this.width + this.margin.right - 12) + ", " + this.height / 2 + ") rotate(90)")
      .attr("font-size", 10)
      .attr("class", "text-current")
      .append("text")
      .attr("text-anchor", "middle")
      .text("kWh");
  }

  disconnect() {
    this.element.innerHTML = "";
  }

  get meterReadings() {
    if (!this.meterReadingsValue || this.meterReadingsValue.length === 0) {
      return [];
    }

    const readings = this.meterReadingsValue
      .map((reading) => {
        return {
          timestamp: moment(reading.timestamp),
          reading_kwh: reading.reading_kwh,
          mid_certified: reading.mid_certified,
        };
      })
      .sort((a, b) => a.timestamp.diff(b.timestamp));

    const firstReading = readings[0].reading_kwh;

    return readings.map((reading) => {
      return {
        ...reading,
        reading_kwh: reading.reading_kwh - firstReading,
      };
    });
  }

  get certifiedMeterReadings() {
    return this.meterReadings.filter((meterReading) => meterReading.mid_certified);
  }

  get uncertifiedMeterReadings() {
    return this.meterReadings.filter((meterReading) => !meterReading.mid_certified);
  }

  get height() {
    return 200 - this.margin.top - this.margin.bottom;
  }

  get width() {
    return this.element.offsetWidth - this.margin.left - this.margin.right;
  }

  get margin() {
    return { top: 0, right: 40, bottom: 35, left: 0 };
  }
}
